/**
 * How the client's assets are split by kind — the data behind the Financial Snapshot's
 * composition bar.
 *
 * WHY IT ONLY EVER COVERS PERSONALLY-OWNED ASSETS. The bar sits directly under the card's
 * "Total Assets" figure, so it has to decompose exactly that number or it is a lie told in
 * colour. That figure comes from `computeStructureSnapshotBreakdown`, which counts personally
 * owned assets plus the home in one pool and apportions each trust/company separately. Rather
 * than try to reconcile against those apportioned entity lines, the caller hides the bar
 * entirely for clients who have structures — that card already shows a per-entity breakdown,
 * which is its own composition view. See FinancialSnapshot's `showStructureSplit`.
 *
 * The family home is INCLUDED, and usually dominates. That is the point: for most clients
 * approaching aged care, "nearly everything is in the house" is the fact that shapes every
 * funding conversation, and a bar that hid it would flatter the liquid position.
 */
import type { FactFind } from '../../../shared/src/calculation/factFindTypes.ts';
import { villageInterestToday } from '../../../shared/src/calculation/support/villageExit.ts';
import type { DraftFactFind } from '../constants/initialFactFind.ts';

/** Ownership values that mean "the applicants hold this themselves", per structureSnapshotUtils. */
const PERSONAL_OWNERSHIP = new Set(['client', 'partner', 'joint']);

export interface AssetCompositionSlice {
    id: string;
    label: string;
    /** Dollars. */
    amount: number;
    /** Share of the total, 0-100, rounded to one decimal — the bar's flex-basis. */
    pct: number;
    /** From the app's own brand ramp, darkest (home) to lightest (other). */
    color: string;
}

/**
 * Asset types grouped into the handful of kinds worth telling apart at a glance. Ordered
 * darkest-first so the bar reads as a ramp rather than a scatter of colours.
 */
const GROUPS: { id: string; label: string; color: string; types: string[] }[] = [
    { id: 'home', label: 'Family home', color: '#001E7A', types: [] },
    { id: 'village', label: 'Village unit (what comes back)', color: '#1E3A8A', types: [] },
    { id: 'super', label: 'Super & pension', color: '#0F52E0', types: ['superannuation', 'accountBasedPensions'] },
    { id: 'shares', label: 'Shares, funds & bonds', color: '#0290F5', types: ['shares', 'investmentBonds'] },
    { id: 'property', label: 'Investment property', color: '#38bdf8', types: ['investmentProperty'] },
    { id: 'cash', label: 'Cash & deposits', color: '#60a5fa', types: ['bankAccounts', 'termDeposits'] },
    { id: 'other', label: 'Other assets', color: '#2dd4bf', types: ['annuities', 'funeralBonds', 'prepaidFuneral', 'loansToOthers', 'personalAssets', 'other'] },
];

/**
 * @returns the non-empty slices, largest first, each carrying its share of the total. An empty
 * array when there is nothing to show — the caller also suppresses a single-slice bar, which
 * says nothing a total does not.
 */
export function assetComposition(factFind: DraftFactFind | null | undefined): AssetCompositionSlice[] {
    if (!factFind) return [];

    const totals: Record<string, number> = {};

    // The home, unless it is held through a structure — the same carve-out the snapshot's own
    // total makes, so the two agree.
    const home = factFind.home || {};
    const homeValue = Number(home.value) || 0;
    if (homeValue > 0 && home.ownership !== 'other_entity') {
        totals.home = homeValue;
    }
    // A village interest at what the village would pay back today (8 Oct 2026) — the snapshot's
    // total carries it, so the bar must too or the two stop adding up.
    const village = villageInterestToday(factFind as unknown as FactFind);
    if (village > 0) totals.village = village;

    const groupOfType = new Map<string, string>();
    GROUPS.forEach((g) => g.types.forEach((t) => groupOfType.set(t, g.id)));

    Object.entries(factFind.assets || {}).forEach(([type, assetList]) => {
        const groupId = groupOfType.get(type);
        if (!groupId) return;
        (assetList || []).forEach((asset) => {
            const value = Number(asset?.value) || 0;
            if (!value) return;
            if (!PERSONAL_OWNERSHIP.has(String(asset?.ownership))) return;
            totals[groupId] = (totals[groupId] || 0) + value;
        });
    });

    const total = Object.values(totals).reduce((sum, v) => sum + v, 0);
    if (total <= 0) return [];

    return GROUPS
        .filter((g) => (totals[g.id] || 0) > 0)
        .map((g) => ({
            id: g.id,
            label: g.label,
            amount: totals[g.id]!,
            pct: Math.round((totals[g.id]! / total) * 1000) / 10,
            color: g.color,
        }))
        .sort((a, b) => b.amount - a.amount);
}

/** Colours for the slices an entity's share adds — a violet ramp so they read apart from the personal kinds. */
const ENTITY_COLORS = ['#7c3aed', '#a78bfa', '#c4b5fd', '#8b5cf6'];

/**
 * The bar with a trust's or company's share beside what is owned individually (owner, 16 Oct 2026:
 * the bar vanished the moment a file had a trust). Each entity's share is the figure the snapshot
 * prints for it — the applicants' percentage of its assets, net of its debts — so the bar adds up to
 * the snapshot's own total. Percentages are recomputed over the whole.
 */
export function withEntityShares(
    slices: AssetCompositionSlice[],
    entityLines: Array<{ id: string; name: string; applicantsShare: number }>,
): AssetCompositionSlice[] {
    const extra = entityLines
        .filter((line) => line.applicantsShare > 0)
        .map((line, i) => ({ id: `entity-${line.id}`, label: `Via ${line.name} (their share)`, amount: line.applicantsShare, pct: 0, color: ENTITY_COLORS[i % ENTITY_COLORS.length]! }));
    const all = [...slices, ...extra];
    const total = all.reduce((sum, sl) => sum + sl.amount, 0);
    if (total <= 0) return [];
    return all.map((sl) => ({ ...sl, pct: Math.round((sl.amount / total) * 1000) / 10 })).sort((a, b) => b.amount - a.amount);
}
